// src/services/api.js
import { finDeSession, estRefusAuth, fermerSession } from './authInterceptor';
import { avecCle } from './cleLecture';

const API_URL = 'https://besoins-tat-production.up.railway.app';

class APIService {
  static getToken() {
    return localStorage.getItem('token');
  }

  static getHeaders(includeAuth = true, isFormData = false) {
    const headers = {};
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }
    return headers;
  }

  static async handleResponse(response) {
    if (!response.ok) {
      let corps = null;
      try { corps = await response.clone().json(); } catch (e) { /* réponse non JSON */ }
      // 401 = fin de session → purge + reload. MAIS seulement si c'en est une :
      // le serveur normalise ses refus d'authentification (`deconnexion: true`
      // + un `code`), et la règle vit dans authInterceptor — un seul endroit.
      //
      // Avant, tout 401 éjectait dès qu'un jeton existait : un 401 venu
      // d'ailleurs (route qui vérifie un secret, relais, service tiers)
      // déconnectait quelqu'un dont la session était valable. Et la purge se
      // faisait SANS motif : l'écran de connexion revenait muet, sans rien à
      // lire ni à raconter. Le 401 d'un échec de connexion, lui, doit toujours
      // remonter au formulaire, qui sait l'afficher.
      if (response.status === 401 && this.getToken() && estRefusAuth(corps)) {
        fermerSession(corps && corps.error, response.url || null, corps);
        return;
      }
      throw new Error((corps && (corps.error || corps.message)) || `Erreur ${response.status}`);
    }
    try {
      return await response.json();
    } catch (e) {
      return { success: true };
    }
  }

  // =========================
  // AUTHENTIFICATION
  // =========================
  // Résolution d'une organisation par code client (login façon Boond).
  // GET /auth/org?code=tat → { success, org: { id, nom, slug } } | 404
  static async getOrg(code) {
    const response = await fetch(`${API_URL}/auth/org?code=${encodeURIComponent(code)}`, {
      headers: this.getHeaders(false)
    });
    return this.handleResponse(response);
  }

  static async login(username, password, customerCode) {
    try {
      const body = { username, password };
      if (customerCode) body.customerCode = customerCode; // optionnel — rétro-compatible
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: this.getHeaders(false),
        body: JSON.stringify(body)
      });
      // Ne passe PAS par le chemin « session expirée » de handleResponse : un 401
      // ici = mauvais identifiants → le message doit s'afficher, jamais recharger.
      if (!response.ok) {
        const j = await response.json().catch(() => ({}));
        throw new Error(j.error || j.message || (response.status === 401 ? 'Identifiants invalides' : `Erreur ${response.status}`));
      }
      const data = await response.json().catch(() => ({}));
      if (data.success && data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.organisation) localStorage.setItem('organisation', JSON.stringify(data.organisation));
        else localStorage.removeItem('organisation');
      }
      return data;
    } catch (error) {
      console.error('Erreur login:', error.message);
      throw error;
    }
  }

  static getOrganisation() {
    try { const raw = localStorage.getItem('organisation'); return raw ? JSON.parse(raw) : null; } catch { return null; }
  }

  // Produit en marque blanche : aucun nom de client ne doit être écrit en dur.
  // Tout texte visible ou document généré passe par le nom de l'organisation.
  static getOrgName(fallback = 'Notre société') {
    const o = this.getOrganisation();
    return o?.nom || o?.name || o?.raison_sociale || o?.libelle || fallback;
  }

  // =========================
  // GESTION DES UTILISATEURS
  // =========================
  static async getUsers() {
    try {
      const response = await fetch(`${API_URL}/users`, {
        headers: this.getHeaders()
      });
      const res = await this.handleResponse(response);
      return { users: res.data || [], roles_admis: res.roles_admis || [] };
    } catch (error) {
      console.error('Erreur récupération utilisateurs:', error.message);
      throw error;
    }
  }

  // =========================
  // GESTION DES BESOINS
  // =========================
  static async getBesoins(filters = {}) {
    try {
      const cleanFilters = Object.entries(filters)
        .filter(([_, value]) => value !== '' && value != null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
      const params = new URLSearchParams(cleanFilters).toString();
      const url = params ? `${API_URL}/besoins?${params}` : `${API_URL}/besoins`;
      const response = await fetch(url, { headers: this.getHeaders() });
      const res = await this.handleResponse(response);
      return { besoins: res.data || [] };
    } catch (error) {
      console.error('Erreur récupération besoins:', error.message);
      throw error;
    }
  }

  static async getBesoin(id) {
    try {
      const response = await fetch(`${API_URL}/besoins/${id}`, {
        headers: this.getHeaders()
      });
      const res = await this.handleResponse(response);
      const besoin = res.data || res;
      if (besoin && besoin.responsable && !besoin.responsable_nom) {
        besoin.responsable_nom = besoin.responsable.username;
      }
      return besoin;
    } catch (error) {
      console.error('Erreur récupération besoin:', error.message);
      throw error;
    }
  }

  static async createBesoin(data) {
    try {
      const response = await fetch(`${API_URL}/besoins`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });
      const res = await this.handleResponse(response);
      return res.data || res;
    } catch (error) {
      console.error('Erreur création besoin:', error.message);
      throw error;
    }
  }

  static async updateBesoin(id, data) {
    try {
      const response = await fetch(`${API_URL}/besoins/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });
      const res = await this.handleResponse(response);
      return res.data || res;
    } catch (error) {
      console.error('Erreur mise à jour besoin:', error.message);
      throw error;
    }
  }

  static async deleteBesoin(id) {
    try {
      const response = await fetch(`${API_URL}/besoins/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression besoin:', error.message);
      throw error;
    }
  }

  // =========================
  // GESTION DES CANDIDATS
  // =========================
  static async getCandidats(besoinId) {
    try {
      const response = await fetch(`${API_URL}/candidats/besoin/${besoinId}`, {
        headers: this.getHeaders()
      });
      const res = await this.handleResponse(response);
      const liste = res.data || res;
      // LA LISTE PEUT ÊTRE INCOMPLÈTE, et le serveur le dit. On accroche
      // l'avertissement à la liste plutôt que de changer la signature :
      // l'appelant qui n'en fait rien continue de recevoir un tableau.
      // Sans lui, un besoin de vingt profils s'afficherait vide sans un mot
      // (vécu 22.09), et on repositionnerait par-dessus ce qui y est déjà.
      if (Array.isArray(liste) && res.lecture_partielle) {
        Object.defineProperty(liste, 'lecturePartielle',
          { value: res.lecture_partielle, enumerable: false });
      }
      // LE CATALOGUE DES COUPES, servi avec la liste qu'il décore. Même
      // accrochage : l'écran propose les distinctions que le serveur déclare,
      // il n'en code aucune liste — en ajouter une, c'est une ligne côté
      // serveur, et elle apparaît ici sans rien toucher.
      if (Array.isArray(liste) && Array.isArray(res.distinctions)) {
        Object.defineProperty(liste, 'distinctions',
          { value: res.distinctions, enumerable: false });
      }
      return liste;
    } catch (error) {
      console.error('Erreur récupération candidats:', error.message);
      throw error;
    }
  }

  static async createCandidat(data) {
    try {
      const response = await fetch(`${API_URL}/candidats`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });
      const res = await this.handleResponse(response);
      return res.data || res;
    } catch (error) {
      console.error('Erreur création candidat:', error.message);
      throw error;
    }
  }

  static async updateCandidat(id, data) {
    try {
      const response = await fetch(`${API_URL}/candidats/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data)
      });
      const res = await this.handleResponse(response);
      return res.data || res;
    } catch (error) {
      console.error('Erreur mise à jour candidat:', error.message);
      throw error;
    }
  }

  static async deleteCandidat(id) {
    try {
      const response = await fetch(`${API_URL}/candidats/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur suppression candidat:', error.message);
      throw error;
    }
  }

  // =========================
  // UPLOAD DE FICHIERS
  // =========================

  // Upload CV vers /upload/cv/candidat/:candidatId (route existante qui fonctionne)
  static async uploadCV(file, candidatName, besoinId, candidatId) {
    try {
      const formData = new FormData();
      formData.append('cv', file);
      formData.append('candidatName', candidatName);
      formData.append('besoinId', besoinId.toString());

      const response = await fetch(`${API_URL}/upload/cv/candidat/${candidatId}`, {
        method: 'POST',
        headers: this.getHeaders(true, true),
        body: formData
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur upload CV:', error.message);
      throw error;
    }
  }

  static async uploadMultipleCV(files, besoinId) {
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('cvs', file);
      });
      formData.append('besoinId', besoinId.toString());
      const response = await fetch(`${API_URL}/upload/multiple`, {
        method: 'POST',
        headers: this.getHeaders(true, true),
        body: formData
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur upload multiple CV:', error.message);
      throw error;
    }
  }

  // =========================
  // UTILITAIRES
  // =========================
  // UN JETON PRÉSENT N'EST PAS UN JETON VIVANT. Avant, on ne regardait que sa
  // PRÉSENCE : on rouvrait l'app le lendemain, l'écran complet s'affichait,
  // puis les panneaux tombaient un par un jusqu'à ce que l'un d'eux déclenche
  // la déconnexion. Lire la date de fin écrite dans le jeton évite ce moment
  // où l'application a l'air de marcher sans marcher.
  //
  // Ce n'est PAS un contrôle de sécurité — la signature n'est pas vérifiée, et
  // elle n'a pas à l'être ici : le serveur refuse de toute façon. C'est de
  // l'affichage, et un jeton illisible laisse le comportement d'avant (on
  // tente, le serveur tranche).
  static isAuthenticated() {
    const token = this.getToken();
    const user = localStorage.getItem('user');
    if (!token || !user) return false;
    const fin = finDeSession();
    return !(fin && fin <= Date.now());
  }

  static getCurrentUser() {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Erreur parsing user data:', error);
      return null;
    }
  }

  static logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('organisation');
  }

  static async testConnection() {
    try {
      const response = await fetch(`${API_URL}/health`);
      return response.ok;
    } catch (error) {
      console.error('Test connexion API échoué:', error);
      return false;
    }
  }
}

// ── Une URL servie par l'API, ouverte HORS fetch ───────────────────────────
// Un <a href>, une <iframe> ou un <img src> ne peuvent pas porter d'en-tête
// Authorization : le jeton passe donc en query, et les routes concernées
// l'acceptent (middleware allowQueryToken). Sans ça, ouvrir un CV répond
// « Token d'authentification requis » — vécu 09.09 sur le CV d'une
// proposition sourceur.
//
// L'URL vient du SERVEUR avec, parfois, ses propres paramètres (?apercu=1) :
// on ajoute donc le jeton avec le bon séparateur plutôt qu'un « ? » aveugle.
// Une URL vide reste vide — on ne fabrique pas un lien vers nulle part.
// 28.09 : c'est la CLÉ DE LECTURE (30 minutes, lecture seule) qui voyage dans
// l'adresse, plus jamais le jeton de session — voir services/cleLecture. Le
// nom reste pour les appelants.
export function urlAvecJeton(url) {
  if (!url) return url;
  return avecCle(url);
}

export default APIService;